import { useState, useEffect, useCallback } from 'react';
import { pm } from '../../services/api';

/* ── helpers ──────────────────────────────────────────────────────────────── */
const fmtDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  return dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const STATUS_META = {
  open:      { label: 'Open',      color: '#3B82F6', bg: '#EFF6FF' },
  completed: { label: 'Completed', color: '#16A34A', bg: '#DCFCE7' },
  missed:    { label: 'Missed',    color: '#DC2626', bg: '#FEE2E2' },
};

const COLORS = ['#8B5CF6','#3B82F6','#EC4899','#F97316','#14B8A6','#F59E0B','#22C55E','#EF4444'];

/* ── Progress bar ─────────────────────────────────────────────────────────── */
function ProgressBar({ pct, color = '#8B5CF6' }) {
  return (
    <div style={{ background: '#E5E7EB', borderRadius: 99, height: 6, overflow: 'hidden' }}>
      <div style={{
        width: `${Math.min(100, pct || 0)}%`, height: '100%',
        background: color, borderRadius: 99, transition: 'width .4s',
      }} />
    </div>
  );
}

/* ── Milestone Card ───────────────────────────────────────────────────────── */
function MilestoneCard({ milestone, onEdit, onDelete, onOpen }) {
  const sm   = STATUS_META[milestone.status] || STATUS_META.open;
  const pct  = milestone.completion_pct ?? 0;
  const over = milestone.is_overdue;

  return (
    <div
      onClick={() => onOpen(milestone)}
      style={{
        background: '#fff', borderRadius: 12,
        border: `1px solid ${over ? '#FECACA' : '#E5E7EB'}`,
        padding: '18px 20px', cursor: 'pointer',
        transition: 'box-shadow .15s',
        boxShadow: '0 1px 3px rgba(0,0,0,.06)',
      }}
      onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,.1)'}
      onMouseLeave={e => e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,.06)'}
    >
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10 }}>
        <div style={{
          width: 10, height: 10, borderRadius: '50%',
          background: milestone.color, flexShrink: 0, marginTop: 5,
        }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 15, color: '#111827', marginBottom: 4 }}>
            {milestone.name}
          </div>
          {milestone.description && (
            <div style={{ fontSize: 13, color: '#6B7280', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {milestone.description}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
          <span style={{
            fontSize: 11, fontWeight: 500, padding: '2px 8px', borderRadius: 99,
            background: sm.bg, color: sm.color,
          }}>
            {sm.label}
          </span>
          <button
            onClick={e => { e.stopPropagation(); onEdit(milestone); }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 6px', borderRadius: 6, color: '#6B7280', fontSize: 14 }}
            title="Edit"
          >✎</button>
          <button
            onClick={e => { e.stopPropagation(); onDelete(milestone); }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 6px', borderRadius: 6, color: '#EF4444', fontSize: 14 }}
            title="Delete"
          >✕</button>
        </div>
      </div>

      {/* Progress */}
      <ProgressBar pct={pct} color={milestone.color} />
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: 12, color: '#6B7280' }}>
        <span>{pct}% complete</span>
        <span>{milestone.completed_issues} / {milestone.total_issues} issues</span>
      </div>

      {/* Payment info */}
      {(milestone.payment_percentage != null || milestone.planned_start_date || milestone.planned_end_date) && (
        <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: '0.5rem 1.25rem', fontSize: 12, color: '#6B7280' }}>
          {milestone.planned_start_date && <span>Start: {fmtDate(milestone.planned_start_date)}</span>}
          {milestone.planned_end_date   && <span>End: {fmtDate(milestone.planned_end_date)}</span>}
          {milestone.payment_percentage != null && (
            <span style={{ color: '#5B21B6', fontWeight: 600 }}>
              💰 {milestone.payment_percentage}%
              {milestone.payment_amount != null && ` — ₹${Number(milestone.payment_amount).toLocaleString('en-IN')}`}
            </span>
          )}
        </div>
      )}

      {/* Footer */}
      <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 16, fontSize: 12, color: '#6B7280' }}>
        {milestone.due_date && <span>Due: {fmtDate(milestone.due_date)}</span>}
        {over && milestone.status === 'open' && (
          <span style={{ color: '#DC2626', fontWeight: 500 }}>Overdue</span>
        )}
        {milestone.milestone_number && (
          <span style={{ marginLeft: 'auto', fontWeight: 700, color: '#8B5CF6', background: '#F5F3FF', padding: '1px 7px', borderRadius: 9999 }}>{milestone.milestone_number}</span>
        )}
      </div>
    </div>
  );
}

/* ── Milestone Form Modal ─────────────────────────────────────────────────── */
function MilestoneModal({ projectId, milestone, project, onClose, onSaved }) {
  const isEdit = !!milestone;
  const [form, setForm] = useState({
    name:               milestone?.name               ?? '',
    description:        milestone?.description        ?? '',
    start_date:         milestone?.start_date         ?? '',
    due_date:           milestone?.due_date           ?? '',
    planned_start_date: milestone?.planned_start_date ?? '',
    planned_end_date:   milestone?.planned_end_date   ?? '',
    payment_percentage: milestone?.payment_percentage != null ? String(milestone.payment_percentage) : '',
    status:             milestone?.status             ?? 'open',
    color:              milestone?.color              ?? '#8B5CF6',
  });
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState(null);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const pctVal = parseFloat(form.payment_percentage) || 0;
  const calcAmt = project?.total_value && pctVal
    ? (parseFloat(project.total_value) * pctVal / 100).toLocaleString('en-IN', { maximumFractionDigits: 2 })
    : null;

  const submit = async () => {
    if (!form.name.trim()) { setError('Name is required'); return; }
    setSaving(true); setError(null);
    try {
      const payload = {
        ...form,
        start_date:         form.start_date         || null,
        due_date:           form.due_date           || null,
        planned_start_date: form.planned_start_date || null,
        planned_end_date:   form.planned_end_date   || null,
        payment_percentage: form.payment_percentage !== '' ? parseFloat(form.payment_percentage) : null,
      };
      if (isEdit) await pm.updateMilestone(milestone.id, payload);
      else        await pm.createMilestone(projectId, payload);
      onSaved();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const inp = {
    width: '100%', padding: '8px 12px', border: '1px solid #D1D5DB',
    borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box',
    background: '#F9FAFB',
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1200,
    }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{
        background: '#fff', borderRadius: 16, padding: 28, width: 480,
        boxShadow: '0 20px 60px rgba(0,0,0,.2)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#111827' }}>
            {isEdit ? 'Edit Milestone' : 'New Milestone'}
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#6B7280' }}>✕</button>
        </div>

        {error && <div style={{ background: '#FEE2E2', color: '#DC2626', padding: '8px 12px', borderRadius: 8, marginBottom: 14, fontSize: 13 }}>{error}</div>}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4, display: 'block' }}>Name *</label>
            <input style={inp} placeholder="Milestone name" value={form.name} onChange={e => set('name', e.target.value)} />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4, display: 'block' }}>Description</label>
            <textarea style={{ ...inp, resize: 'vertical', minHeight: 70 }} placeholder="Optional description..." value={form.description} onChange={e => set('description', e.target.value)} />
          </div>
          {/* Planned dates */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4, display: 'block' }}>Planned Start Date *</label>
              <input type="date" style={inp} value={form.planned_start_date || ''} onChange={e => set('planned_start_date', e.target.value)} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4, display: 'block' }}>Planned End Date *</label>
              <input type="date" style={inp} value={form.planned_end_date || ''} onChange={e => set('planned_end_date', e.target.value)} />
            </div>
          </div>

          {/* Payment */}
          <div style={{ background: '#F5F3FF', border: '1px solid #DDD6FE', borderRadius: 8, padding: '10px 14px' }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4, display: 'block' }}>Payment Percentage (% of project value)</label>
            <input type="number" min="0" max="100" step="0.1" style={inp} placeholder="e.g. 25"
              value={form.payment_percentage} onChange={e => set('payment_percentage', e.target.value)} />
            {calcAmt && (
              <p style={{ margin: '4px 0 0', fontSize: 12, color: '#5B21B6', fontWeight: 600 }}>
                ≈ ₹{calcAmt} for this milestone
              </p>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4, display: 'block' }}>Actual Start Date</label>
              <input type="date" style={inp} value={form.start_date || ''} onChange={e => set('start_date', e.target.value)} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4, display: 'block' }}>Actual Due Date</label>
              <input type="date" style={inp} value={form.due_date || ''} onChange={e => set('due_date', e.target.value)} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4, display: 'block' }}>Status</label>
              <select style={inp} value={form.status} onChange={e => set('status', e.target.value)}>
                <option value="open">Open</option>
                <option value="completed">Completed</option>
                <option value="missed">Missed</option>
              </select>
            </div>
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6, display: 'block' }}>Color</label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {COLORS.map(c => (
                <div
                  key={c} onClick={() => set('color', c)}
                  style={{
                    width: 24, height: 24, borderRadius: '50%', background: c,
                    cursor: 'pointer',
                    outline: form.color === c ? `3px solid ${c}` : '3px solid transparent',
                    outlineOffset: 2,
                  }}
                />
              ))}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 24 }}>
          <button onClick={onClose} style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid #D1D5DB', background: '#fff', cursor: 'pointer', fontSize: 14 }}>Cancel</button>
          <button
            onClick={submit} disabled={saving}
            style={{ padding: '8px 18px', borderRadius: 8, border: 'none', background: '#8B5CF6', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600, opacity: saving ? .7 : 1 }}
          >
            {saving ? 'Saving…' : (isEdit ? 'Save Changes' : 'Create Milestone')}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Issue Picker for Milestone ───────────────────────────────────────────── */
function IssuePickerModal({ milestone, projectIssues, assignedIds, onClose, onSaved }) {
  const [selected, setSelected] = useState([]);
  const [saving,   setSaving]   = useState(false);
  const available = projectIssues.filter(i => !assignedIds.includes(i.id));

  const toggle = (id) => setSelected(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);

  const submit = async () => {
    if (!selected.length) return;
    setSaving(true);
    try {
      await pm.addIssuesToMilestone(milestone.id, { issue_ids: selected });
      onSaved();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1300,
    }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: '#fff', borderRadius: 16, padding: 28, width: 460, boxShadow: '0 20px 60px rgba(0,0,0,.2)', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#111827' }}>Add Issues to "{milestone.name}"</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#6B7280' }}>✕</button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {available.length === 0 && (
            <div style={{ textAlign: 'center', color: '#6B7280', padding: '32px 0', fontSize: 14 }}>All issues already assigned</div>
          )}
          {available.map(issue => (
            <div
              key={issue.id}
              onClick={() => toggle(issue.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px',
                borderRadius: 8, cursor: 'pointer', marginBottom: 4,
                background: selected.includes(issue.id) ? '#F5F3FF' : 'transparent',
              }}
            >
              <input type="checkbox" checked={selected.includes(issue.id)} onChange={() => toggle(issue.id)} style={{ accentColor: '#8B5CF6' }} />
              <span style={{ fontSize: 12, color: '#6B7280', fontFamily: 'monospace' }}>{issue.sequence_id}</span>
              <span style={{ fontSize: 14, color: '#111827' }}>{issue.title}</span>
              {issue.state_name && (
                <span style={{ marginLeft: 'auto', fontSize: 11, padding: '2px 8px', borderRadius: 99, background: '#F3F4F6', color: '#374151' }}>
                  {issue.state_name}
                </span>
              )}
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
          <button onClick={onClose} style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid #D1D5DB', background: '#fff', cursor: 'pointer', fontSize: 14 }}>Cancel</button>
          <button
            onClick={submit} disabled={saving || !selected.length}
            style={{ padding: '8px 18px', borderRadius: 8, border: 'none', background: '#8B5CF6', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600, opacity: (saving || !selected.length) ? .5 : 1 }}
          >
            {saving ? 'Adding…' : `Add ${selected.length || ''} Issue${selected.length !== 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Milestone Detail Panel ───────────────────────────────────────────────── */
function MilestoneDetail({ milestone, projectIssues, onClose, onRefresh }) {
  const [issues,     setIssues]     = useState([]);
  const [loadingIss, setLoadingIss] = useState(true);
  const [showPicker, setShowPicker] = useState(false);

  const loadIssues = useCallback(async () => {
    setLoadingIss(true);
    try {
      const r = await pm.getMilestoneIssues(milestone.id);
      setIssues(r.data);
    } finally {
      setLoadingIss(false);
    }
  }, [milestone.id]);

  useEffect(() => { loadIssues(); }, [loadIssues]);

  const removeIssue = async (issueId) => {
    await pm.removeIssueFromMilestone(milestone.id, issueId);
    loadIssues();
    onRefresh();
  };

  const sm  = STATUS_META[milestone.status] || STATUS_META.open;
  const pct = milestone.completion_pct ?? 0;

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)',
      display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end', zIndex: 1200,
    }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{
        width: 520, height: '100%', background: '#fff',
        boxShadow: '-8px 0 32px rgba(0,0,0,.15)',
        display: 'flex', flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{ padding: '24px 24px 16px', borderBottom: '1px solid #E5E7EB' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <div style={{ width: 12, height: 12, borderRadius: '50%', background: milestone.color }} />
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#111827', flex: 1 }}>{milestone.name}</h2>
            <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#6B7280' }}>✕</button>
          </div>
          {milestone.description && (
            <p style={{ margin: '0 0 10px', fontSize: 14, color: '#6B7280' }}>{milestone.description}</p>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', fontSize: 13, color: '#6B7280' }}>
            <span style={{ padding: '3px 10px', borderRadius: 99, background: sm.bg, color: sm.color, fontWeight: 500, fontSize: 12 }}>{sm.label}</span>
            <span>Due: {fmtDate(milestone.due_date)}</span>
            {milestone.is_overdue && milestone.status === 'open' && <span style={{ color: '#DC2626', fontWeight: 500 }}>Overdue</span>}
          </div>
        </div>

        {/* Progress */}
        <div style={{ padding: '16px 24px', borderBottom: '1px solid #E5E7EB' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 13, color: '#374151' }}>
            <span style={{ fontWeight: 600 }}>Progress</span>
            <span>{pct}% ({milestone.completed_issues}/{milestone.total_issues} issues)</span>
          </div>
          <ProgressBar pct={pct} color={milestone.color} />
        </div>

        {/* Issues list */}
        <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '14px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontWeight: 600, fontSize: 14, color: '#374151' }}>Issues</span>
            <button
              onClick={() => setShowPicker(true)}
              style={{ padding: '5px 12px', borderRadius: 8, border: '1px solid #8B5CF6', background: '#F5F3FF', color: '#7C3AED', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
            >
              + Add Issues
            </button>
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: '0 24px 24px' }}>
            {loadingIss ? (
              <div style={{ textAlign: 'center', padding: 32, color: '#9CA3AF' }}>Loading…</div>
            ) : issues.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '32px 0', color: '#9CA3AF', fontSize: 14 }}>No issues added yet</div>
            ) : (
              issues.map(issue => (
                <div key={issue.id} style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                  borderRadius: 8, border: '1px solid #F3F4F6', marginBottom: 6, background: '#FAFAFA',
                }}>
                  <span style={{
                    width: 8, height: 8, borderRadius: '50%',
                    background: issue.state_color || '#9CA3AF', flexShrink: 0,
                  }} />
                  <span style={{ fontSize: 12, color: '#6B7280', fontFamily: 'monospace', flexShrink: 0 }}>{issue.sequence_id}</span>
                  <span style={{ flex: 1, fontSize: 14, color: '#111827', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{issue.title}</span>
                  {issue.state_name && (
                    <span style={{ fontSize: 11, padding: '2px 7px', borderRadius: 99, background: '#F3F4F6', color: '#374151', flexShrink: 0 }}>{issue.state_name}</span>
                  )}
                  <button
                    onClick={() => removeIssue(issue.id)}
                    style={{ background: 'none', border: 'none', color: '#EF4444', cursor: 'pointer', fontSize: 14, padding: '0 4px', flexShrink: 0 }}
                    title="Remove"
                  >✕</button>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {showPicker && (
        <IssuePickerModal
          milestone={milestone}
          projectIssues={projectIssues}
          assignedIds={issues.map(i => i.id)}
          onClose={() => setShowPicker(false)}
          onSaved={() => { setShowPicker(false); loadIssues(); onRefresh(); }}
        />
      )}
    </div>
  );
}

/* ── Milestone Timeline View ──────────────────────────────────────────────── */
function MilestoneTimeline({ milestones }) {
  const DAY_W   = 28;
  const DAYS    = 120;
  const ROW_H   = 72;   // tall enough for two separate bars
  const ms1Day  = 86400000;
  const today   = new Date(); today.setHours(0,0,0,0);
  const startOf = new Date(today); startOf.setDate(today.getDate() - 14);

  const days = Array.from({ length: DAYS }, (_, i) => {
    const d = new Date(startOf); d.setDate(startOf.getDate() + i); return d;
  });

  const todayIdx = Math.round((today - startOf) / ms1Day);

  const dayIdx = (iso) => {
    if (!iso) return null;
    const diff = Math.round((new Date(iso) - startOf) / ms1Day);
    return diff >= 0 && diff < DAYS ? diff : (diff < 0 ? 0 : DAYS - 1);
  };

  const months = [];
  days.forEach((d, i) => {
    const mo = d.toLocaleDateString('en-GB', { month: 'short', year: '2-digit' });
    if (!months.length || months[months.length - 1].label !== mo)
      months.push({ label: mo, idx: i });
  });

  /* bar vertical positions — planned on top, actual below */
  const PLAN_TOP   = 10;   // top px of planned bar
  const PLAN_H     = 13;
  const ACTUAL_TOP = 32;   // top px of actual bar  (gap of 9px between bars)
  const ACTUAL_H   = 13;

  if (!milestones.length) return (
    <div style={{ textAlign: 'center', padding: '4rem', color: '#9CA3AF' }}>No milestones to display</div>
  );

  return (
    <div style={{ padding: '1rem 1.5rem', overflowX: 'auto' }}>
      <div style={{ minWidth: DAY_W * DAYS + 240, background: '#fff',
                    border: '1px solid #E5E7EB', borderRadius: 12, overflow: 'hidden' }}>

        {/* Month row */}
        <div style={{ display: 'flex', borderBottom: '1px solid #E5E7EB', background: '#F9FAFB' }}>
          <div style={{ width: 240, flexShrink: 0, borderRight: '1px solid #E5E7EB',
                        padding: '6px 12px', fontSize: '0.7rem', fontWeight: 700, color: '#374151' }}>
            Milestone
          </div>
          <div style={{ position: 'relative', width: DAY_W * DAYS, flexShrink: 0, height: 28 }}>
            {months.map((m, i) => (
              <span key={i} style={{ position: 'absolute', left: m.idx * DAY_W + 4, top: 6,
                                     fontSize: '0.65rem', fontWeight: 700, color: '#6B7280', whiteSpace: 'nowrap' }}>
                {m.label}
              </span>
            ))}
          </div>
        </div>

        {/* Day header */}
        <div style={{ display: 'flex', borderBottom: '2px solid #E5E7EB', background: '#F9FAFB' }}>
          {/* Lane labels column */}
          <div style={{ width: 240, flexShrink: 0, borderRight: '1px solid #E5E7EB',
                        display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
                        paddingBottom: 4, paddingLeft: 12, gap: 2 }}>
            <span style={{ fontSize: '0.58rem', color: '#9CA3AF', fontWeight: 600, letterSpacing: '0.04em' }}>PLANNED ─────</span>
            <span style={{ fontSize: '0.58rem', color: '#9CA3AF', fontWeight: 600, letterSpacing: '0.04em' }}>ACTUAL ──────</span>
          </div>
          <div style={{ display: 'flex' }}>
            {days.map((d, i) => {
              const isToday = i === todayIdx;
              const isMon   = d.getDay() === 1;
              return (
                <div key={i} style={{
                  width: DAY_W, flexShrink: 0, textAlign: 'center', fontSize: '0.58rem',
                  fontWeight: isToday ? 800 : 400,
                  color: isToday ? '#7C3AED' : isMon ? '#374151' : '#D1D5DB',
                  padding: '3px 0',
                  background: isToday ? '#F5F3FF' : 'transparent',
                  borderRight: isMon ? '1px solid #E5E7EB' : '1px solid #F3F4F6',
                }}>
                  {isMon || isToday ? d.getDate() : ''}
                </div>
              );
            })}
          </div>
        </div>

        {/* Milestone rows */}
        {milestones.map(ms => {
          const sm = STATUS_META[ms.status] || STATUS_META.open;

          // Planned span: start_date → due_date
          const planStart = dayIdx(ms.start_date || ms.created_at);
          const planEnd   = dayIdx(ms.due_date);
          const planW     = planEnd != null && planStart != null ? Math.max(planEnd - planStart, 1) : 1;

          // Actual span: start → today (open) or start → due (completed/missed)
          // Width is capped to completion_pct proportion of the planned span
          const pct        = ms.completion_pct || 0;
          const actualW    = planStart != null ? Math.max(Math.round(planW * pct / 100), pct > 0 ? 1 : 0) : 0;

          const isOverdue = ms.is_overdue && ms.status === 'open';

          return (
            <div key={ms.id}
              style={{ display: 'flex', borderBottom: '1px solid #F3F4F6', minHeight: ROW_H }}
              onMouseEnter={e => e.currentTarget.style.background = '#FAFAFA'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>

              {/* Label column — wider to show Planned / Actual sub-labels */}
              <div style={{ width: 240, flexShrink: 0, borderRight: '1px solid #E5E7EB',
                            padding: '8px 12px', display: 'flex', flexDirection: 'column',
                            justifyContent: 'center', gap: 2, overflow: 'hidden' }}>
                {/* Name row */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: ms.color, flexShrink: 0 }} />
                  <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#111827',
                                 overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {ms.name}
                  </span>
                </div>
                {/* Status + pct */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, paddingLeft: 14, marginTop: 1 }}>
                  <span style={{ fontSize: '0.6rem', padding: '1px 7px', borderRadius: 99,
                                 background: sm.bg, color: sm.color, fontWeight: 600 }}>{sm.label}</span>
                  <span style={{ fontSize: '0.6rem', color: '#9CA3AF' }}>{pct}%</span>
                  {isOverdue && <span style={{ fontSize: '0.6rem', color: '#DC2626', fontWeight: 700 }}>Overdue</span>}
                </div>
                {/* Planned date range */}
                <div style={{ paddingLeft: 14, marginTop: 3 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span style={{ fontSize: '0.56rem', fontWeight: 700, color: `${ms.color}99`,
                                   background: `${ms.color}15`, padding: '1px 5px', borderRadius: 3,
                                   letterSpacing: '0.03em' }}>PLAN</span>
                    <span style={{ fontSize: '0.58rem', color: '#9CA3AF' }}>
                      {fmtDate(ms.start_date)} → {fmtDate(ms.due_date)}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 }}>
                    <span style={{ fontSize: '0.56rem', fontWeight: 700, color: '#fff',
                                   background: ms.color, padding: '1px 5px', borderRadius: 3,
                                   letterSpacing: '0.03em' }}>ACTUAL</span>
                    <span style={{ fontSize: '0.58rem', color: '#9CA3AF' }}>
                      {ms.completed_issues}/{ms.total_issues} issues
                    </span>
                  </div>
                </div>
              </div>

              {/* Gantt area */}
              <div style={{ width: DAY_W * DAYS, flexShrink: 0, position: 'relative', height: ROW_H }}>

                {/* Today vertical line */}
                <div style={{ position: 'absolute', top: 0, bottom: 0,
                              left: todayIdx * DAY_W + DAY_W / 2,
                              width: 1, background: '#7C3AED40', pointerEvents: 'none' }} />

                {/* ── PLANNED BAR (top lane) ── */}
                {planStart != null && planEnd != null && (
                  <div
                    title={`Planned: ${fmtDate(ms.start_date || ms.created_at)} → ${fmtDate(ms.due_date)}`}
                    style={{
                      position: 'absolute',
                      top: PLAN_TOP, height: PLAN_H,
                      left: planStart * DAY_W + 2,
                      width: planW * DAY_W - 4,
                      background: `${ms.color}22`,
                      border: `1.5px dashed ${ms.color}80`,
                      borderRadius: 4,
                      display: 'flex', alignItems: 'center',
                      paddingLeft: 6, overflow: 'hidden',
                    }}
                  >
                    <span style={{ fontSize: '0.55rem', fontWeight: 600, color: ms.color,
                                   whiteSpace: 'nowrap', opacity: 0.9 }}>
                      Planned
                    </span>
                  </div>
                )}

                {/* ── ACTUAL BAR (bottom lane) ── */}
                {planStart != null && actualW > 0 && (
                  <div
                    title={`Actual progress: ${pct}%`}
                    style={{
                      position: 'absolute',
                      top: ACTUAL_TOP, height: ACTUAL_H,
                      left: planStart * DAY_W + 2,
                      width: actualW * DAY_W - 4,
                      background: `linear-gradient(90deg, ${ms.color}cc, ${ms.color})`,
                      borderRadius: 4,
                      display: 'flex', alignItems: 'center',
                      paddingLeft: 6, overflow: 'hidden',
                      boxShadow: `0 2px 6px ${ms.color}44`,
                    }}
                  >
                    <span style={{ fontSize: '0.55rem', fontWeight: 700, color: '#fff', whiteSpace: 'nowrap' }}>
                      {pct}%
                    </span>
                  </div>
                )}

                {/* Empty actual lane placeholder (so lane is always visible) */}
                {planStart != null && actualW === 0 && (
                  <div style={{
                    position: 'absolute',
                    top: ACTUAL_TOP, height: ACTUAL_H,
                    left: planStart != null ? planStart * DAY_W + 2 : 2,
                    width: planW * DAY_W - 4,
                    background: '#F3F4F6',
                    border: '1px solid #E5E7EB',
                    borderRadius: 4,
                  }} />
                )}

                {/* Lane separator line */}
                <div style={{
                  position: 'absolute',
                  top: PLAN_TOP + PLAN_H + 4,
                  left: 0, right: 0,
                  height: 1, background: '#F3F4F6',
                  pointerEvents: 'none',
                }} />

                {/* Due date diamond marker (sits between the two bars) */}
                {planEnd != null && (
                  <div
                    title={`Due: ${fmtDate(ms.due_date)}`}
                    style={{
                      position: 'absolute',
                      top: PLAN_TOP + PLAN_H + 1,   // sits on the separator line between bars
                      left: planEnd * DAY_W + DAY_W / 2 - 7,
                      width: 13, height: 13,
                      background: isOverdue ? '#DC2626' : ms.color,
                      transform: 'rotate(45deg)', borderRadius: 2,
                      border: isOverdue ? '2px solid #991B1B' : `1.5px solid ${ms.color}`,
                      zIndex: 2,
                    }}
                  />
                )}

              </div>
            </div>
          );
        })}

        {/* Legend */}
        <div style={{ padding: '10px 16px', background: '#F9FAFB', borderTop: '1px solid #E5E7EB',
                      display: 'flex', gap: 20, fontSize: '0.68rem', color: '#6B7280', alignItems: 'center',
                      flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 700, color: '#374151' }}>Legend:</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 32, height: 9, background: '#8B5CF622', border: '1.5px dashed #8B5CF680', borderRadius: 3, display: 'inline-block' }} />
            <span>Planned duration <span style={{ color: '#9CA3AF' }}>(top bar)</span></span>
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 32, height: 9, background: 'linear-gradient(90deg,#8B5CF6cc,#8B5CF6)', borderRadius: 3, display: 'inline-block' }} />
            <span>Actual progress <span style={{ color: '#9CA3AF' }}>(bottom bar)</span></span>
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 11, height: 11, background: '#8B5CF6', transform: 'rotate(45deg)', display: 'inline-block', borderRadius: 1 }} />
            Due date
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 11, height: 11, background: '#DC2626', transform: 'rotate(45deg)', display: 'inline-block', borderRadius: 1 }} />
            Overdue
          </span>
        </div>
      </div>
    </div>
  );
}

/* ── Main Milestones component ────────────────────────────────────────────── */
export default function Milestones({ project, onRefresh }) {
  const projectId = project?.id;

  const [milestones, setMilestones] = useState([]);
  const [allIssues,  setAllIssues]  = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [showForm,   setShowForm]   = useState(false);
  const [editingMs,  setEditingMs]  = useState(null);
  const [detailMs,   setDetailMs]   = useState(null);
  const [filter,     setFilter]     = useState('all');
  const [msView,     setMsView]     = useState('timeline'); // 'cards' | 'timeline'

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const [msRes, issRes] = await Promise.all([
        pm.getMilestones(projectId),
        pm.getIssues(projectId),
      ]);
      setMilestones(msRes.data);
      setAllIssues(issRes.data);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const deleteMilestone = async (ms) => {
    if (!window.confirm(`Delete milestone "${ms.name}"? This cannot be undone.`)) return;
    await pm.deleteMilestone(ms.id);
    load();
  };

  const grouped = {
    open:      milestones.filter(m => m.status === 'open'),
    completed: milestones.filter(m => m.status === 'completed'),
    missed:    milestones.filter(m => m.status === 'missed'),
  };
  const filtered = filter === 'all' ? milestones : milestones.filter(m => m.status === filter);

  return (
    <div style={{ padding: 28, maxWidth: msView === 'timeline' ? '100%' : 960, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#111827' }}>Milestones</h2>
          <p style={{ margin: '4px 0 0', fontSize: 14, color: '#6B7280' }}>
            Track key delivery checkpoints for {project?.name}
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {/* View toggle */}
          <div style={{ display: 'flex', background: '#F3F4F6', borderRadius: 8, padding: 2, gap: 1 }}>
            {[['cards', '▦ Cards'], ['timeline', '↔ Timeline']].map(([k, label]) => (
              <button key={k} onClick={() => setMsView(k)}
                style={{
                  padding: '5px 14px', borderRadius: 6, border: 'none', cursor: 'pointer',
                  fontSize: 13, fontWeight: msView === k ? 700 : 400,
                  background: msView === k ? '#fff' : 'transparent',
                  color: msView === k ? '#111827' : '#6B7280',
                  boxShadow: msView === k ? '0 1px 4px rgba(0,0,0,.08)' : 'none',
                }}>
                {label}
              </button>
            ))}
          </div>
          <button
            onClick={() => { setEditingMs(null); setShowForm(true); }}
            style={{
              padding: '9px 18px', borderRadius: 10, border: 'none',
              background: '#8B5CF6', color: '#fff', fontWeight: 600, fontSize: 14,
              cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
            }}
          >
            + New Milestone
          </button>
        </div>
      </div>

      {/* Stats row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 24 }}>
        {[
          { label: 'Open',      key: 'open',      color: '#3B82F6', bg: '#EFF6FF' },
          { label: 'Completed', key: 'completed', color: '#16A34A', bg: '#DCFCE7' },
          { label: 'Missed',    key: 'missed',    color: '#DC2626', bg: '#FEE2E2' },
        ].map(s => (
          <div
            key={s.key}
            style={{ background: s.bg, borderRadius: 12, padding: '16px 20px', cursor: 'pointer', border: `2px solid ${filter === s.key ? s.color : 'transparent'}` }}
            onClick={() => setFilter(f => f === s.key ? 'all' : s.key)}
          >
            <div style={{ fontSize: 28, fontWeight: 700, color: s.color }}>{grouped[s.key].length}</div>
            <div style={{ fontSize: 13, color: s.color, fontWeight: 500, marginTop: 2 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filter pills */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {[
          { key: 'all',       label: `All (${milestones.length})` },
          { key: 'open',      label: `Open (${grouped.open.length})` },
          { key: 'completed', label: `Completed (${grouped.completed.length})` },
          { key: 'missed',    label: `Missed (${grouped.missed.length})` },
        ].map(f => (
          <button
            key={f.key} onClick={() => setFilter(f.key)}
            style={{
              padding: '5px 14px', borderRadius: 99, border: '1px solid',
              borderColor: filter === f.key ? '#8B5CF6' : '#E5E7EB',
              background: filter === f.key ? '#F5F3FF' : '#fff',
              color: filter === f.key ? '#7C3AED' : '#6B7280',
              fontSize: 13, fontWeight: 500, cursor: 'pointer',
            }}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '64px 0', color: '#9CA3AF', fontSize: 15 }}>Loading milestones…</div>
      ) : msView === 'timeline' ? (
        <MilestoneTimeline milestones={milestones} />
      ) : filtered.length === 0 ? (
        <div style={{
          textAlign: 'center', padding: '64px 0', color: '#9CA3AF',
          background: '#fff', borderRadius: 16, border: '2px dashed #E5E7EB',
        }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🎯</div>
          <div style={{ fontSize: 16, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
            {filter === 'all' ? 'No milestones yet' : `No ${filter} milestones`}
          </div>
          <div style={{ fontSize: 14, color: '#9CA3AF', marginBottom: 20 }}>
            Milestones help you track key checkpoints in your project
          </div>
          {filter === 'all' && (
            <button
              onClick={() => { setEditingMs(null); setShowForm(true); }}
              style={{ padding: '9px 20px', borderRadius: 10, border: 'none', background: '#8B5CF6', color: '#fff', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}
            >
              Create first milestone
            </button>
          )}
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
          {filtered.map(ms => (
            <MilestoneCard
              key={ms.id}
              milestone={ms}
              onEdit={m => { setEditingMs(m); setShowForm(true); }}
              onDelete={deleteMilestone}
              onOpen={m => setDetailMs(m)}
            />
          ))}
        </div>
      )}

      {/* Create/Edit modal */}
      {showForm && (
        <MilestoneModal
          projectId={projectId}
          milestone={editingMs}
          project={project}
          onClose={() => { setShowForm(false); setEditingMs(null); }}
          onSaved={() => { setShowForm(false); setEditingMs(null); load(); onRefresh?.(); }}
        />
      )}

      {/* Detail panel */}
      {detailMs && (
        <MilestoneDetail
          milestone={detailMs}
          projectIssues={allIssues}
          onClose={() => setDetailMs(null)}
          onRefresh={() => { load(); onRefresh?.(); }}
        />
      )}
    </div>
  );
}
